import { yupResolver } from '@hookform/resolvers/yup';
import {
  Badge,
  BadgeShape,
  BadgeSize,
  Button,
  ButtonLevel,
  ButtonSize,
  SelectedFile,
  SelectedFileStatus,
  Tab,
  TabList,
  TabPanel,
  TabVariant,
  Tabs,
  Text,
  TextColor,
  TextSize,
  TextWeight,
} from '@vizit/atoms';
import { FormTextField } from '@vizit/forms';
import { PageIcon } from '@vizit/icons';
import { Dialog, type DialogProps, DropZone } from '@vizit/molecules';
import { borderColor } from '@vizit/theme';
import { formatFileSize } from '@vizit/utils';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { STATUS_LABELS, buildTemplateContent, downloadTemplate, statusToBadgeLevel } from './invite-users-dialog.utils';
import { BulkInviteEmailStatus, type BulkInvitePreviewRow, isBulkInviteGrantError } from './types';

const MAX_FILE_SIZE = 3 * 1024 * 1024; // 3MB (mirrors bulk-import; server hard-caps at 100 emails)
const MIN_FILE_SIZE = 1; // 1B — allow tiny single-email CSVs (DropZone defaults to 2KB otherwise)
const ACCEPTED_FILE_TYPES = ['text/csv'];

const VALIDATE_FALLBACK = 'We could not validate that file. Please check it and try again.';
const DISPATCH_FALLBACK = 'We could not send those invites. Please try again in a few minutes.';

// Collapses a burst of picker clicks (each scope toggle changes `bulkRevalidateKey`) into one
// re-validation, so adjusting the selection cannot fire an upload per click.
const REVALIDATE_DEBOUNCE_MS = 400;

// Shared by both tab panels so the dialog keeps one size across tabs.
const TAB_PANEL_HEIGHT = 'min-h-[260px]';
const EMPTY_DROP_ZONE_HEIGHT = 'min-h-[118px]';
const SELECTED_FILE_HEIGHT = 'min-h-[144px]';

type InviteEmailFormSchema = { email: string };
type InviteTab = 'email' | 'csv';

interface InviteUsersDialogProps extends Omit<DialogProps, 'children'> {
  /** The name of the organization, shown in the subtitle copy. */
  organizationName?: string;
  /**
   * "By email" submit handler — unchanged from the single-invite flow.
   * @param data The email to invite.
   * @param onSuccess Call to close the dialog.
   * @param onError Call to surface an inline error on the email field.
   */
  onInviteSubmit: (data: InviteEmailFormSchema, onSuccess: () => void, onError: () => void) => Promise<void>;
  /** Extra fields rendered under the email input (e.g. role/scope for hierarchical orgs). */
  inviteAccessFields?: React.ReactNode;
  /** Fired when the dialog resets, so the caller can clear the state behind `inviteAccessFields`. */
  onInviteReset?: () => void;
  /** "Upload CSV" seam 1 — server-authoritative validation of the uploaded file → preview rows. */
  onValidateFile: (file: File) => Promise<BulkInvitePreviewRow[]>;
  /** "Upload CSV" seam 2 — dispatch the uploaded file. Reject to keep the dialog open. */
  onBulkSubmit: (file: File) => Promise<void>;
  /**
   * Role/scope fields for the Upload CSV tab, rendered above the drop zone — one batch grant for
   * every email in the file. When this slot is present, errors thrown as `BulkInviteGrantError`
   * by either seam are assumed to be rendered inside it, so the dialog suppresses its own
   * inline message for them.
   */
  bulkAccessFields?: React.ReactNode;
  /**
   * Fingerprint of the grant selection behind `bulkAccessFields` — empty string while the
   * selection is incomplete. When it changes while a file is selected, the current preview is
   * discarded (it no longer reflects what dispatch would send); a non-empty key then re-validates
   * the file (debounced), while an empty key only clears — validating an incomplete selection
   * would just fail fast and nag the user mid-edit.
   */
  bulkRevalidateKey?: string;
  /**
   * Fired when the selected CSV is removed, so the `bulkAccessFields` owner can clear any
   * grant error it is rendering about the discarded attempt.
   */
  onBulkFileRemoved?: () => void;
}

/**
 * Unified "Invite users" dialog: a By email / Upload CSV toggle. "By email" keeps the
 * existing single-invite flow; "Upload CSV" uploads a CSV, reviews a server-validated
 * preview, and dispatches. Presentational only — both flows are owned by the caller.
 */
function InviteUsersDialog(props: InviteUsersDialogProps) {
  const {
    isOpen,
    onOpenChange,
    organizationName,
    onInviteSubmit,
    inviteAccessFields,
    onInviteReset,
    onValidateFile,
    onBulkSubmit,
    bulkAccessFields,
    bulkRevalidateKey,
    onBulkFileRemoved,
    ...other
  } = props;

  const [selectedTab, setSelectedTab] = React.useState<InviteTab>('email');

  // ── By email ────────────────────────────────────────────────────────────────
  const emailForm = useForm<InviteEmailFormSchema>({
    resolver: yupResolver(
      yup.object().shape({
        email: yup.string().email('Please enter a valid email.').required('Please enter an email.'),
      }),
    ),
    defaultValues: { email: '' },
    mode: 'all',
    delayError: 650,
  });
  const isEmailSubmitting = emailForm.formState.isSubmitting;

  // ── Upload CSV ───────────────────────────────────────────────────────────────
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  // null = no completed successful validation (nothing validated yet, or the last attempt failed).
  // Distinct from [] so the empty-file hint below only shows for a real zero-row validation —
  // never alongside a failure whose message is rendered elsewhere (e.g. a grant error on the picker).
  const [previewRows, setPreviewRows] = React.useState<BulkInvitePreviewRow[] | null>(null);
  const [isValidating, setIsValidating] = React.useState(false);
  const [isBulkSubmitting, setIsBulkSubmitting] = React.useState(false);
  const [bulkError, setBulkError] = React.useState<string | null>(null);
  const validateRequestId = React.useRef(0);

  const templateContent = buildTemplateContent();
  const validCount = React.useMemo(
    () => (previewRows ?? []).filter((row) => row.status === BulkInviteEmailStatus.Valid).length,
    [previewRows],
  );
  const hasValidEmails = validCount > 0;
  const hasFileButNoValid = selectedFile != null && !isValidating && (previewRows?.length ?? 0) > 0 && !hasValidEmails;
  // Defensive only: a caller whose `onValidateFile` resolves with no rows. The live wiring never
  // gets here — the server rejects an email-less CSV, so that reason arrives as `bulkError`.
  const hasFileButEmpty = selectedFile != null && !isValidating && previewRows != null && previewRows.length === 0;

  const isSubmitting = isEmailSubmitting || isBulkSubmitting;

  // A boolean, not the node itself: inline JSX gets a fresh identity every parent render, which
  // would rebuild this callback (and everything depending on it) for no behavioral reason.
  const hasBulkAccessFields = bulkAccessFields != null;

  // Grant errors are rendered by the `bulkAccessFields` owner; only render them here when that
  // slot is absent and there is nowhere else to show the reason.
  const toBulkErrorMessage = React.useCallback(
    (error: unknown, fallback: string): string | null => {
      if (isBulkInviteGrantError(error) && hasBulkAccessFields) {
        return null;
      }
      return error instanceof Error && error.message ? error.message : fallback;
    },
    [hasBulkAccessFields],
  );

  const resetAll = React.useCallback(() => {
    validateRequestId.current += 1;
    setSelectedFile(null);
    setPreviewRows(null);
    setBulkError(null);
    setIsValidating(false);
    emailForm.reset({ email: '' });
    onInviteReset?.();
  }, [emailForm, onInviteReset]);

  const handleFilesChange = React.useCallback(
    async (files: File[]) => {
      validateRequestId.current += 1;
      const requestId = validateRequestId.current;
      const file = files[0] ?? null;
      setSelectedFile(file);
      setBulkError(null);
      setPreviewRows(null);

      if (!file) {
        return;
      }

      setIsValidating(true);
      try {
        const rows = await onValidateFile(file);
        if (validateRequestId.current === requestId) {
          setPreviewRows(rows);
        }
      } catch (error) {
        if (validateRequestId.current === requestId) {
          setBulkError(toBulkErrorMessage(error, VALIDATE_FALLBACK));
        }
      } finally {
        if (validateRequestId.current === requestId) {
          setIsValidating(false);
        }
      }
    },
    [onValidateFile, toBulkErrorMessage],
  );

  // React to grant-selection changes: `onValidateFile` reads the selection at call time, so an
  // already-validated preview goes stale the moment the selection changes. Any key change
  // discards the current preview and any in-flight validation; a complete selection (non-empty
  // key) then re-validates after a debounce, while an incomplete one only clears — validating it
  // would just fail fast and nag the user mid-edit (e.g. right after a role change reset the
  // scopes). The nag is reserved for explicit attempts: dropping a file or pressing Send.
  const lastRevalidateKey = React.useRef(bulkRevalidateKey);
  React.useEffect(() => {
    if (lastRevalidateKey.current === bulkRevalidateKey) {
      return;
    }
    lastRevalidateKey.current = bulkRevalidateKey;
    if (!selectedFile) {
      return;
    }

    validateRequestId.current += 1;
    setPreviewRows(null);
    setBulkError(null);
    // The invalidated request's own `finally` no longer matches the request id, so reset here.
    setIsValidating(false);

    if (!bulkRevalidateKey) {
      return;
    }
    const timer = setTimeout(() => void handleFilesChange([selectedFile]), REVALIDATE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [bulkRevalidateKey, selectedFile, handleFilesChange]);

  const handleRemoveFile = React.useCallback(() => {
    validateRequestId.current += 1;
    setSelectedFile(null);
    setPreviewRows(null);
    setBulkError(null);
    setIsValidating(false);
    // A picker error describes the discarded attempt; its owner must clear it too.
    onBulkFileRemoved?.();
  }, [onBulkFileRemoved]);

  const handleDialogOpenChange = React.useCallback(
    (open: boolean) => {
      if (!open && isSubmitting) {
        return;
      }
      if (!open) {
        resetAll();
      }
      onOpenChange?.(open);
    },
    [onOpenChange, isSubmitting, resetAll],
  );

  const closeAfterSuccess = React.useCallback(() => {
    resetAll();
    onOpenChange?.(false);
  }, [resetAll, onOpenChange]);

  const onEmailValid = React.useCallback(
    async (data: InviteEmailFormSchema) => {
      await onInviteSubmit(
        data,
        () => closeAfterSuccess(),
        () =>
          emailForm.setError('email', {
            message: 'We are having trouble inviting this user, try again in a few minutes.',
            type: 'manual',
          }),
      );
    },
    [onInviteSubmit, closeAfterSuccess, emailForm],
  );

  const submitBulk = React.useCallback(async () => {
    if (!selectedFile || !hasValidEmails) {
      return;
    }
    setIsBulkSubmitting(true);
    setBulkError(null);
    try {
      await onBulkSubmit(selectedFile);
      closeAfterSuccess();
    } catch (error) {
      setBulkError(toBulkErrorMessage(error, DISPATCH_FALLBACK));
    } finally {
      setIsBulkSubmitting(false);
    }
  }, [selectedFile, hasValidEmails, onBulkSubmit, closeAfterSuccess, toBulkErrorMessage]);

  const handleSendInvites = React.useCallback(() => {
    if (selectedTab === 'email') {
      void emailForm.handleSubmit(onEmailValid)();
    } else {
      void submitBulk();
    }
  }, [selectedTab, emailForm, onEmailValid, submitBulk]);

  const sendDisabled =
    selectedTab === 'email'
      ? !emailForm.formState.isValid || isEmailSubmitting
      : !hasValidEmails || isValidating || isBulkSubmitting;

  return (
    <Dialog isOpen={isOpen} onOpenChange={handleDialogOpenChange} {...other}>
      <Dialog.Overlay>
        <Dialog.Content dialogClassName="gap-12" closeButtonIsDisabled={isSubmitting}>
          <Dialog.Header>
            <Dialog.Title>Invite users</Dialog.Title>
            <Dialog.Subtitle color={TextColor.Tertiary}>
              {`Invite users to ${
                organizationName ? `${organizationName}'s account` : 'your account'
              } by email, or upload a CSV to invite multiple users at once.`}
            </Dialog.Subtitle>
          </Dialog.Header>

          <Dialog.Body className="px-12">
            <Tabs
              variant={TabVariant.Default}
              className="w-full"
              selectedKey={selectedTab}
              onSelectionChange={(key) => setSelectedTab(key as InviteTab)}
              // Both tabs edit the same role/scope selection, and a dispatch reads it when it
              // starts — switching tabs mid-submit could show a different grant than the one
              // being applied (and fire a re-validation against it).
              isDisabled={isSubmitting}
            >
              {/* Toggle stays compact (w-fit); the panels below fill the dialog width so the
                  dialog size does not change when switching tabs. */}
              <TabList className="w-fit" aria-label="Invite method">
                <Tab id="email">By email</Tab>
                <Tab id="csv">Upload CSV</Tab>
              </TabList>

              <TabPanel id="email" className={TAB_PANEL_HEIGHT}>
                <div className="space-y-8">
                  <FormTextField
                    control={emailForm.control}
                    name="email"
                    label="Email"
                    type="email"
                    inputProps={{ 'data-1p-ignore': 'true', 'data-lpignore': 'true' }}
                    isDisabled={isEmailSubmitting}
                    isRequired
                  />
                  {inviteAccessFields}
                </div>
              </TabPanel>

              <TabPanel id="csv" className={TAB_PANEL_HEIGHT}>
                <div className="space-y-12">
                  {/* Template download */}
                  <div className="flex items-center justify-between p-5 border border-secondary rounded-xs">
                    <div className="flex items-center gap-5 pl-5 pr-2">
                      <div className="flex flex-col gap-2">
                        <PageIcon size={40} color={borderColor.secondary} />
                        <div className="relative bottom-15 right-3">
                          <div className="px-[3px] py-1 absolute bg-success-700">
                            <Text className="text-white text-[10px] font-bold leading-normal">CSV</Text>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col">
                        <Text className="text-sm font-medium">Vizit_Bulk_Invite_Users.csv</Text>
                        <Text color={TextColor.Tertiary} className="text-xs">
                          {formatFileSize(templateContent.length)}
                        </Text>
                      </div>
                    </div>
                    <Button level={ButtonLevel.Secondary} size={ButtonSize.Sm} onPress={() => downloadTemplate()}>
                      Download
                    </Button>
                  </div>

                  {/* One batch grant for the whole file — before the drop zone, because
                      validation reads the selection and would fail on an incomplete grant. */}
                  {bulkAccessFields}

                  {/* Upload CSV */}
                  <div className="space-y-5">
                    <Text size={TextSize.Sm} weight={TextWeight.Regular} color={TextColor.Secondary}>
                      Upload CSV
                    </Text>
                    <DropZone.Provider
                      acceptedFileTypes={ACCEPTED_FILE_TYPES}
                      allowsMultiple={false}
                      maxFileSize={MAX_FILE_SIZE}
                      minFileSize={MIN_FILE_SIZE}
                      defaultFiles={selectedFile ? [selectedFile] : []}
                      onFilesChange={handleFilesChange}
                    >
                      <DropZone aria-label="Upload CSV" className={EMPTY_DROP_ZONE_HEIGHT} />
                      <DropZone.SelectedFiles
                        aria-label="Selected CSV file"
                        className={selectedFile ? SELECTED_FILE_HEIGHT : undefined}
                      >
                        {({ file, onRemove, className }) => (
                          <>
                            <SelectedFile
                              file={file}
                              status={
                                isValidating || isBulkSubmitting
                                  ? SelectedFileStatus.Uploading
                                  : SelectedFileStatus.Ready
                              }
                              onRemove={() => {
                                handleRemoveFile();
                                onRemove(file);
                              }}
                              className={className}
                            />
                            {/* Status and errors sit inside the file box, as in `BulkImportDialog`. */}
                            {isValidating && (
                              <Text
                                elementType="output"
                                size={TextSize.Sm}
                                color={TextColor.Tertiary}
                                showEllipsis={false}
                              >
                                Validating emails…
                              </Text>
                            )}
                            {bulkError && (
                              <Text role="alert" size={TextSize.Sm} color={TextColor.Danger} showEllipsis={false}>
                                {bulkError}
                              </Text>
                            )}
                          </>
                        )}
                      </DropZone.SelectedFiles>
                    </DropZone.Provider>

                    {!isValidating && previewRows != null && previewRows.length > 0 && (
                      <BulkInvitePreview rows={previewRows} />
                    )}

                    {hasFileButNoValid && (
                      <Text elementType="output" size={TextSize.Sm} color={TextColor.Tertiary} showEllipsis={false}>
                        No valid emails found in this file. Fix the highlighted rows and upload again.
                      </Text>
                    )}

                    {hasFileButEmpty && (
                      <Text elementType="output" size={TextSize.Sm} color={TextColor.Tertiary} showEllipsis={false}>
                        No emails found in this file. Add at least one email and upload again.
                      </Text>
                    )}
                  </div>
                </div>
              </TabPanel>
            </Tabs>
          </Dialog.Body>

          <Dialog.Footer>
            <Button
              className="w-full"
              size={ButtonSize.Lg}
              level={ButtonLevel.Secondary}
              onPress={() => handleDialogOpenChange(false)}
              isDisabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              className="w-full"
              size={ButtonSize.Lg}
              level={ButtonLevel.Primary}
              onPress={handleSendInvites}
              isDisabled={sendDisabled}
              isLoading={isSubmitting}
            >
              Send invites
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Overlay>
    </Dialog>
  );
}

interface BulkInvitePreviewProps {
  rows: BulkInvitePreviewRow[];
}

function BulkInvitePreview(props: BulkInvitePreviewProps) {
  const { rows } = props;
  const validCount = rows.filter((row) => row.status === BulkInviteEmailStatus.Valid).length;
  const skippedCount = rows.length - validCount;

  return (
    <div className="space-y-3">
      <Text size={TextSize.Sm} color={TextColor.Secondary} showEllipsis={false}>
        {`${validCount} ready to invite`}
        {skippedCount > 0 ? `, ${skippedCount} will be skipped` : ''}
      </Text>
      <div className="max-h-[240px] overflow-y-auto border border-secondary rounded-xs">
        <table className="w-full text-left border-collapse">
          <caption className="sr-only">Preview of the emails to invite and their status</caption>
          <thead className="sticky top-0 bg-white">
            <tr>
              <th scope="col" className="px-5 py-3">
                <Text size={TextSize.Xs} color={TextColor.Tertiary} weight={TextWeight.Medium}>
                  Email
                </Text>
              </th>
              <th scope="col" className="px-5 py-3 text-right">
                <Text size={TextSize.Xs} color={TextColor.Tertiary} weight={TextWeight.Medium}>
                  Status
                </Text>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={`${row.email}-${index}`} className="border-t border-secondary">
                <td className="px-5 py-3">
                  <Text size={TextSize.Sm} showEllipsis>
                    {row.email}
                  </Text>
                </td>
                <td className="px-5 py-3 text-right">
                  <Badge level={statusToBadgeLevel(row.status)} shape={BadgeShape.Box} size={BadgeSize.Sm}>
                    {STATUS_LABELS[row.status]}
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export { InviteUsersDialog, BulkInvitePreview };
export type { InviteUsersDialogProps, BulkInvitePreviewProps, InviteEmailFormSchema };
